import { useRef, useState } from "react";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatPrice } from "@/lib/pricingUtils";
import { SERVICES } from "@/constants/testIds";

const STATUS_LABELS = { new: "Nuevo", duplicate: "Duplicado", created: "Creado", updated: "Actualizado", skipped: "Omitido", error: "Error" };

/** Importacion masiva de servicios: pegar/subir JSON -> vista previa
 * (marca nuevos vs. duplicados, con Omitir/Actualizar por duplicado) ->
 * confirmar -> resumen. Nunca borra ni toca servicios fuera de la lista. */
export default function ServiceImportModal({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("input"); // input | preview | result
  const [rawText, setRawText] = useState("");
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState([]);
  const [actions, setActions] = useState({}); // index -> "update" | "skip"
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep("input"); setRawText(""); setItems([]); setPreview([]); setActions({}); setResult(null); setError("");
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawText(String(ev.target.result));
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    setError("");
    let parsed;
    try {
      parsed = JSON.parse(rawText);
      if (!Array.isArray(parsed)) throw new Error("El JSON debe ser una lista de servicios.");
    } catch (e) {
      setError(`JSON inválido: ${e.message}`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/services/import", { items: parsed, commit: false });
      setItems(parsed);
      setPreview(data.items);
      setActions({});
      setStep("preview");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      const itemsWithActions = items.map((it, idx) => ({ ...it, action: preview[idx]?.status === "duplicate" ? (actions[idx] || "skip") : undefined }));
      const { data } = await api.post("/services/import", { items: itemsWithActions, commit: true });
      setResult(data);
      setStep("result");
      onImported();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={SERVICES.importModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Importar servicios</DialogTitle>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Pegá un JSON con la lista de servicios, o subí un archivo .json. Vas a poder revisar todo antes de confirmar.
            </p>
            <Textarea
              rows={12}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder='[{"nombre": "...", "categoria": "Inyectables", "descripcion_completa": "...", "precio_valor": 450, "precio_tipo": "fijo", "nota_precio": null, "duracion_min": 30, "duracion_efecto_meses": 4}]'
              className="font-mono text-xs"
              data-testid={SERVICES.importPasteTextarea}
            />
            <div>
              <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileUpload} data-testid={SERVICES.importFileInput} />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-3.5 h-3.5" /> Subir archivo .json
              </Button>
            </div>
            {error && <p className="text-sm text-destructive" data-testid={SERVICES.importError}>{error}</p>}
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {preview.filter((p) => p.status === "new").length} nuevos &middot; {preview.filter((p) => p.status === "duplicate").length} ya existen (elegí qué hacer con cada uno)
            </p>
            <div className="border border-border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table data-testid={SERVICES.importPreviewTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Precio</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.map((p, idx) => (
                    <TableRow key={idx} data-testid={`${SERVICES.importPreviewRowPrefix}${idx}`}>
                      <TableCell className="font-medium">{p.nombre}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{items[idx]?.categoria || "-"}</TableCell>
                      <TableCell className="text-sm">{formatPrice(items[idx]?.precio_valor, items[idx]?.precio_tipo, items[idx]?.nota_precio)}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "new" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {STATUS_LABELS[p.status]}
                        </span>
                      </TableCell>
                      <TableCell>
                        {p.status === "duplicate" ? (
                          <Select value={actions[idx] || "skip"} onValueChange={(v) => setActions((a) => ({ ...a, [idx]: v }))}>
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`${SERVICES.importDuplicateActionSelectPrefix}${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip">Omitir</SelectItem>
                              <SelectItem value="update">Actualizar</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground">Se va a crear</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {error && <p className="text-sm text-destructive" data-testid={SERVICES.importError}>{error}</p>}
          </div>
        )}

        {step === "result" && result && (
          <div className="py-6 text-center space-y-3" data-testid={SERVICES.importResultSummary}>
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="text-lg font-serif text-foreground">Importación completa</p>
            <p className="text-sm text-muted-foreground">
              {result.created} creados &middot; {result.updated} actualizados &middot; {result.skipped} omitidos
            </p>
            {result.items.some((i) => i.status === "error") && (
              <p className="text-sm text-destructive flex items-center justify-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Algunos servicios tuvieron errores, revisá el detalle.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "input" && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} data-testid={SERVICES.importCancelButton}>Cancelar</Button>
              <Button onClick={handlePreview} disabled={loading || !rawText.trim()} data-testid={SERVICES.importPreviewButton}>
                {loading ? "Analizando..." : "Ver vista previa"}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("input")} data-testid={SERVICES.importBackButton}>Volver</Button>
              <Button onClick={handleConfirm} disabled={loading} data-testid={SERVICES.importConfirmButton}>
                {loading ? "Importando..." : "Confirmar importación"}
              </Button>
            </>
          )}
          {step === "result" && (
            <Button onClick={() => handleClose(false)}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
